"use client";

import { NativeLink as Link } from "@/components/native-link";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CalendarDays,
  Database,
  Download,
  FileText,
  Globe2,
  Link2,
  LockKeyhole,
  RefreshCw,
  Search,
  Users,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Select } from "@/components/ui/select";
import { Card, PageHeader, Pill } from "@/components/pulse-shell";
import { api, type AuditEventListItem } from "@/lib/api";
import { hasPermission } from "@/lib/users-rbac";
import { useAuthProfile } from "@/lib/auth-context";

const TABS = [
  { label: "All events", category: "all" },
  { label: "Admin actions", category: "admin" },
  { label: "Data access", category: "data_access" },
  { label: "Authentication", category: "authentication" },
  { label: "System", category: "system" },
] as const;

type RangeKey = "7d" | "30d";

function rangeFrom(key: RangeKey): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - (key === "7d" ? 7 : 30) * 86400000);
  return { from: from.toISOString(), to: to.toISOString() };
}

function formatTime(iso?: string | null) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
}

function initials(display?: string | null, email?: string | null) {
  const s = String(display || email || "?").trim();
  if (!s) return "?";
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

function riskTone(risk?: string | null): "gray" | "orange" | "red" {
  const r = String(risk || "").toLowerCase();
  if (r === "critical" || r === "high") return "red";
  if (r === "medium") return "orange";
  return "gray";
}

function capitalize(s?: string | null) {
  const t = String(s || "");
  if (!t) return "—";
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function outcomeLabel(outcome?: string | null) {
  const o = String(outcome || "success").toLowerCase();
  if (o === "denied" || o === "failure" || o === "failed") return { text: "Denied", ok: false };
  return { text: "Success", ok: true };
}

export function AuditPage() {
  const profile = useAuthProfile();
  const canExport = hasPermission(profile?.permissions, "audit.export");
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState<(typeof TABS)[number]["label"]>("All events");
  const [rangeKey, setRangeKey] = useState<RangeKey>("30d");
  const [risk, setRisk] = useState("all");
  const [live, setLive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [events, setEvents] = useState<AuditEventListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(50);
  const [hasMore, setHasMore] = useState(false);
  const [metrics, setMetrics] = useState({ events: 0, privileged: 0, highRisk: 0, failedAccess: 0 });
  const [exporting, setExporting] = useState(false);

  const category = useMemo(
    () => TABS.find((t) => t.label === tab)?.category || "all",
    [tab],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { from, to } = rangeFrom(rangeKey);
      const out = await api.listAuditEvents({
        from,
        to,
        category,
        risk: risk === "all" ? undefined : risk,
        q: query.trim() || undefined,
        limit,
        offset,
      });
      setEvents(out.events || []);
      setTotal(out.total || 0);
      setHasMore(Boolean(out.hasMore));
      setMetrics(out.metrics || { events: 0, privileged: 0, highRisk: 0, failedAccess: 0 });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load audit events");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [rangeKey, category, risk, query, limit, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!live) return undefined;
    const id = window.setInterval(() => {
      void load();
    }, 15000);
    return () => window.clearInterval(id);
  }, [live, load]);

  async function onExport() {
    if (!canExport) return;
    setExporting(true);
    setError("");
    try {
      const { from, to } = rangeFrom(rangeKey);
      const pack = await api.exportAuditEvents({
        from,
        to,
        category,
        risk: risk === "all" ? undefined : risk,
        q: query.trim() || undefined,
        limit: 500,
      });
      const blob = new Blob([JSON.stringify(pack, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pulse-audit-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Export failed");
    } finally {
      setExporting(false);
    }
  }

  const showingFrom = total === 0 ? 0 : offset + 1;
  const showingTo = offset + events.length;

  return (
    <>
      <PageHeader
        title="Audit log"
        subtitle="Append-only record of administrative actions and sensitive data access."
        actions={
          <>
            <div className="search-wrap">
              <Search />
              <input
                className="field"
                value={query}
                onChange={(e) => {
                  setOffset(0);
                  setQuery(e.target.value);
                }}
                placeholder="Search event, actor or resource"
              />
            </div>
            <Select
              size="sm"
              variant="toolbar"
              searchable={false}
              ariaLabel="Audit date range"
              value={rangeKey === "7d" ? "Last 7 days" : "Last 30 days"}
              onChange={(v) => {
                setOffset(0);
                setRangeKey(v === "Last 7 days" ? "7d" : "30d");
              }}
              options={[
                { value: "Last 30 days", label: "Last 30 days", icon: <CalendarDays size={14} /> },
                { value: "Last 7 days", label: "Last 7 days", icon: <CalendarDays size={14} /> },
              ]}
            />
            {canExport ? (
              <button className="btn primary" type="button" disabled={exporting} onClick={() => void onExport()}>
                <Download />
                {exporting ? "Exporting…" : "Export evidence"}
              </button>
            ) : null}
          </>
        }
      />

      <section className="card ledger-banner">
        <span className="ledger-shield">
          <FileText size={20} />
        </span>
        <span>
          <b>Append-only audit log</b>
          <span>
            {metrics.events.toLocaleString()} events in range · no cryptographic verify in product scope
          </span>
        </span>
        <button className="btn" type="button" onClick={() => void load()} disabled={loading}>
          <RefreshCw />
          Refresh
        </button>
      </section>

      <section className="card audit-metrics">
        <AMetric icon={<FileText />} label="Events" value={String(metrics.events)} color="#0968f2" />
        <AMetric icon={<Users />} label="Privileged actions" value={String(metrics.privileged)} color="#8057e8" />
        <AMetric icon={<AlertTriangle />} label="High-risk events" value={String(metrics.highRisk)} color="#ef4c55" />
        <AMetric icon={<LockKeyhole />} label="Failed access" value={String(metrics.failedAccess)} color="#ef8a0c" />
      </section>

      {error ? <div className="user-settings-error">{error}</div> : null}

      <div className="audit-layout">
        <Card className="audit-stream" title="Event stream">
          <div className="audit-controls">
            <div className="audit-filters">
              <Select
                size="sm"
                variant="toolbar"
                searchable={false}
                ariaLabel="Risk filter"
                value={risk}
                onChange={(v) => {
                  setOffset(0);
                  setRisk(v || "all");
                }}
                options={[
                  { value: "all", label: "All risk" },
                  { value: "low", label: "Low" },
                  { value: "medium", label: "Medium" },
                  { value: "high", label: "High" },
                  { value: "critical", label: "Critical" },
                ]}
              />
              <Pill tone="blue">
                <span className="dot" />
                Production
              </Pill>
            </div>
            <div className="segmented">
              {TABS.map((x) => (
                <button
                  key={x.label}
                  type="button"
                  className={tab === x.label ? "active" : ""}
                  onClick={() => {
                    setOffset(0);
                    setTab(x.label);
                  }}
                >
                  {x.label}
                </button>
              ))}
            </div>
          </div>
          <div className="data-card">
            <table className="data-table audit-table">
              <thead>
                <tr>
                  <th>Time ↓</th>
                  <th>Actor</th>
                  <th>Event</th>
                  <th>Resource</th>
                  <th>Source</th>
                  <th>Result</th>
                  <th>Risk</th>
                  <th>Event ID</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {events.map((e) => {
                  const outcome = outcomeLabel(e.outcome);
                  const riskLabel = capitalize(e.risk);
                  const source = [e.source === "web" ? "Web UI" : e.source || "—", e.sourceIp]
                    .filter(Boolean)
                    .join(" · ");
                  return (
                    <tr
                      key={e.eventId}
                      className={String(e.risk).toLowerCase() === "critical" ? "critical-event" : ""}
                    >
                      <td>{formatTime(e.createdAt)}</td>
                      <td>
                        <div className="audit-actor">
                          <span className="avatar">{initials(e.actorDisplay, e.actorEmail)}</span>
                          <span className="audit-actor-text">
                            <span>{e.actorDisplay || e.actorEmail || "Unknown actor"}</span>
                            {e.actorEmail && e.actorDisplay && e.actorDisplay !== e.actorEmail ? (
                              <small className="muted">{e.actorEmail}</small>
                            ) : null}
                          </span>
                        </div>
                      </td>
                      <td>
                        <Link className="event-link" href={`/audit/${e.eventId}`}>
                          {e.title || e.action}
                        </Link>
                      </td>
                      <td>
                        <span className="audit-resource-text">
                          <span>{e.resourceLabel || "—"}</span>
                          {e.resourceType ? <small className="muted">{e.resourceType}</small> : null}
                        </span>
                      </td>
                      <td>{source}</td>
                      <td>
                        <Pill tone={outcome.ok ? "green" : "red"}>
                          {outcome.ok ? "✓" : "×"} {outcome.text}
                        </Pill>
                      </td>
                      <td>
                        <Pill className="risk-pill" tone={riskTone(e.risk)}>
                          <span className="dot" />
                          {riskLabel}
                        </Pill>
                      </td>
                      <td>{e.eventId}</td>
                      <td>
                        <Link className="row-action" href={`/audit/${e.eventId}`}>
                          ›
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!loading && events.length === 0 ? (
            <div className="empty-state">No audit events match this view.</div>
          ) : null}
          <div className="table-footer">
            <span>
              {loading
                ? "Loading…"
                : `Showing ${showingFrom}–${showingTo} of ${total.toLocaleString()} events`}
            </span>
            <div className="toolbar-group">
              <span>Rows per page</span>
              <Select
                size="sm"
                variant="toolbar"
                searchable={false}
                ariaLabel="Rows per page"
                value={String(limit)}
                onChange={(v) => {
                  setOffset(0);
                  setLimit(Number(v) || 50);
                }}
                options={[
                  { value: "25", label: "25" },
                  { value: "50", label: "50" },
                  { value: "100", label: "100" },
                ]}
              />
              <div className="pagination">
                <button
                  type="button"
                  disabled={offset <= 0 || loading}
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                >
                  ‹
                </button>
                <button type="button" className="active">
                  {Math.floor(offset / limit) + 1}
                </button>
                <button
                  type="button"
                  disabled={!hasMore || loading}
                  onClick={() => setOffset(offset + limit)}
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        </Card>

        <div className="stack">
          <Card title="Risk signals">
            {events
              .filter((e) => ["high", "critical"].includes(String(e.risk || "").toLowerCase()))
              .slice(0, 5)
              .map((e) => (
                <Risk
                  key={e.eventId}
                  color={String(e.risk).toLowerCase() === "critical" ? "#ef4d55" : "#f59e0b"}
                  label={e.title || e.action}
                  time={formatTime(e.createdAt)}
                />
              ))}
            {events.filter((e) => ["high", "critical"].includes(String(e.risk || "").toLowerCase())).length === 0 ? (
              <p className="muted" style={{ margin: 0 }}>
                No high-risk events in this page.
              </p>
            ) : null}
          </Card>
          <Card title="Retention policy">
            <Retention icon={<FileText />} label="Write model" value="Append-only" />
            <Retention icon={<Globe2 />} label="Environment" value="Production" />
            <Retention icon={<Link2 />} label="Integrity verify" value="Out of scope" />
            <Retention icon={<Database />} label="Partitions" value="Daily" />
          </Card>
          <Card title="Auto refresh" action={<Switch checked={live} onCheckedChange={setLive} />}>
            <p className="muted" style={{ margin: 0 }}>
              {live ? "Refreshing every 15 seconds." : "Paused — use Refresh to reload."}
            </p>
          </Card>
        </div>
      </div>
    </>
  );
}

function AMetric({
  icon,
  label,
  value,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  color: string;
}) {
  return (
    <div className="audit-metric">
      <span className="metric-icon" style={{ color, background: `${color}13` }}>
        {icon}
      </span>
      <span>
        {label}
        <b>{value}</b>
      </span>
    </div>
  );
}

function Risk({ color, label, time }: { color: string; label: string; time: string }) {
  return (
    <div className="risk-signal">
      <span className="dot" style={{ color }} />
      <span>{label}</span>
      <time>{time}</time>
    </div>
  );
}

function Retention({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="retention-row">
      {icon}
      <span>{label}</span>
      <b>{value}</b>
    </div>
  );
}
